import React, { useState, useContext, useMemo, useCallback, useEffect } from "react";
import eventEmitter from "shared/events";
import { TaskContext } from "contexts/task";
import { SourcePage, CodingSource, TaskType } from "shared/taskTypeMap";
import DrugIndex from "./drug";
import EnergyIndex from "./energy";
import GuaranteedAnalysisIndex from "./guaranteed-analysis";
import LightingIndex from "./lighting";
import NutritionIndex from "./nutritionpanel";
import SupplementIndex from "./supplement";
import Modal from "../../modal";

import { getDefaultValueByType, readOnlyValue } from "../lib/attribute-data";
import { useParams } from "react-router-dom";
import { AttributesContext } from "contexts/attributes";
import classnames from "classnames";
import { DraggablePanel } from "./components/draggable-panel/draggable-panel";

const NoPanel = (props) => {
    return (
        <div className="txt-center">
            <h4>Panel Component Missing</h4>
            <button
                className="button -primary"
                onClick={() => {
                    props.action.remove();
                    props.action.resolve(false);
                }}
            >
                Close
            </button>
        </div>
    );
};

const getComponent = (type) => {
    switch (type) {
        case "DrugPanel":
            return DrugIndex;
        case "EnergyPanel":
            return EnergyIndex;
        case "GuaranteedAnalysisPanel":
            return GuaranteedAnalysisIndex;
        case "LightingPanel":
            return LightingIndex;
        case "NutritionPanel":
            return NutritionIndex;
        case "SupplementPanel":
            return SupplementIndex;
        default:
            return NoPanel;
    }
};

const PanelLaunch = (props) => {
    const params = useParams();
    const [classNames, setClassNames] = useState("");
    const { task } = useContext(TaskContext);
    const { refresh } = useContext(AttributesContext);
    const [showModal, setShowModal] = useState(false);
    const [showPanel, setShowPanel] = useState(false);
    const taskId = params.id;
    const sourcePage = CodingSource[task.taskType] || SourcePage[task.taskType];
    const isCQANewPanel =
        task.taskType === TaskType.CODING_QA && props.attribute.componentType === "NutritionPanel" && !props.attribute.groups.includes("VarietyPack");

    useEffect(() => {
        if (isCQANewPanel && showPanel) {
            document.body.classList.add("-modal-showing");
        } else {
            document.body.classList.remove("-modal-showing");
        }
    }, [isCQANewPanel, showPanel]);

    const _showModal = () => {
        eventEmitter.emit("PAUSE_WINDOW_VISIBLE", true);
        setShowModal(true);
    };
    const _closeModal = () => {
        setShowModal(false);
        eventEmitter.emit("PAUSE_WINDOW_VISIBLE", false);

        if (task.taskType === TaskType.CODING_QA && !props.skipInternalSave) {
            refresh();
        }
    };

    const togglePanel = (bool) => {
        const useBool = bool ? bool : !showPanel;
        setShowPanel(useBool);

        if (task.taskType === TaskType.CODING_QA && !props.skipInternalSave && !bool) {
            refresh();
        }
    };

    const handleCodingQaUpdates = () => {
        if (task.taskType === TaskType.CODING_QA && !props.skipInternalSave) {
            return props.updateOverride;
        } else {
            return props.updateOnChange;
        }
    };

    const Field = useMemo(() => getComponent(props.dataType), [props.dataType]);
    const useValue = props.defaultValue || getDefaultValueByType(props.attribute.componentType);

    const handleClassNames = useCallback((className) => {
        setClassNames(className);
    }, []);

    if (props.disabled) {
        return <div>{readOnlyValue(useValue, props.attribute.componentType)}</div>;
    }

    if (isCQANewPanel) {
        return (
            <div>
                <button
                    className={classnames("button", { "-pressed": showPanel })}
                    title="Panel"
                    disabled={props.disabled}
                    onClick={() => {
                        togglePanel(!showPanel);
                    }}
                >
                    Edit
                </button>
                {showPanel && (
                    <DraggablePanel
                        togglePanel={togglePanel}
                        roleType="qa"
                        roleLabel="QA Coder"
                        isQaEdit={true}
                    >
                        <Field
                            value={JSON.parse(JSON.stringify(useValue))}
                            attribute={props.attribute}
                            codingWipKey={props.codingWipKey}
                            updateOnChange={handleCodingQaUpdates()}
                            skipInternalSave={props.skipInternalSave}
                            sourcePage={sourcePage}
                            taskId={taskId}
                            isQaEdit={true}
                            togglePanel={togglePanel}
                        />
                    </DraggablePanel>
                )}
            </div>
        );
    }

    return (
        <div>
            <button
                className="button"
                onClick={_showModal}
            >
                Edit
            </button>
            <Modal
                cssClass={classNames}
                show={showModal}
                hideClose={true}
                onClose={_closeModal}
            >
                <Field
                    value={JSON.parse(JSON.stringify(useValue))}
                    attribute={props.attribute}
                    codingWipKey={props.codingWipKey}
                    updateOnChange={handleCodingQaUpdates()}
                    closeModal={_closeModal}
                    skipInternalSave={props.skipInternalSave}
                    sourcePage={sourcePage}
                    taskId={taskId}
                    onUpdateModalStyles={handleClassNames}
                />
            </Modal>
        </div>
    );
};

export default PanelLaunch;
